const SOURCE_CHANGES_REFRESH_PERFORMED_FIELD = "__sourceChangesRefreshPerformed";
const SOURCE_CHANGES_RETRY_ATTEMPTED_FIELD = "__sourceChangesRetryAttempted";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeExactOrderIds(values) {
  if (!Array.isArray(values)) {
    return null;
  }
  const normalizedValues = values.map(normalizeInlineText);
  if (normalizedValues.some((orderId) => !orderId)) {
    return null;
  }
  return [...new Set(normalizedValues)].sort();
}

function readConflictPayloadField(error, fieldName) {
  const payload = error?.payload && typeof error.payload === "object" && !Array.isArray(error.payload)
    ? error.payload
    : null;
  const detail = payload?.detail && typeof payload.detail === "object" && !Array.isArray(payload.detail)
    ? payload.detail
    : null;
  return payload?.[fieldName] ?? detail?.[fieldName];
}

function normalizeStaleCodes(staleCodes) {
  return new Set(
    (Array.isArray(staleCodes) ? staleCodes : [staleCodes])
      .map(normalizeInlineText)
      .filter(Boolean),
  );
}

export function haveSameSourceChangeOrderIds(leftOrderIds = [], rightOrderIds = []) {
  const left = normalizeExactOrderIds(leftOrderIds);
  const right = normalizeExactOrderIds(rightOrderIds);
  return left !== null
    && right !== null
    && left.length === right.length
    && left.every((orderId, index) => orderId === right[index]);
}

export function markSourceChangesRefreshPerformed(error) {
  if (!error || (typeof error !== "object" && typeof error !== "function")) {
    return error;
  }
  try {
    Object.defineProperty(error, SOURCE_CHANGES_REFRESH_PERFORMED_FIELD, {
      configurable: true,
      value: true,
    });
  } catch {
    try {
      error[SOURCE_CHANGES_REFRESH_PERFORMED_FIELD] = true;
    } catch {
      // L'erreur d'origine reste prioritaire si elle est non extensible.
    }
  }
  return error;
}

export function wasSourceChangesRefreshPerformed(error) {
  return error?.[SOURCE_CHANGES_REFRESH_PERFORMED_FIELD] === true;
}

function markSourceChangesRetryAttempted(error) {
  if (!error || (typeof error !== "object" && typeof error !== "function")) {
    return error;
  }
  try {
    Object.defineProperty(error, SOURCE_CHANGES_RETRY_ATTEMPTED_FIELD, {
      configurable: true,
      value: true,
    });
  } catch {
    try {
      error[SOURCE_CHANGES_RETRY_ATTEMPTED_FIELD] = true;
    } catch {
      // L'erreur d'origine reste prioritaire si elle est non extensible.
    }
  }
  return error;
}

export function wasSourceChangesRetryAttempted(error) {
  return error?.[SOURCE_CHANGES_RETRY_ATTEMPTED_FIELD] === true;
}

export function resolveExactSourceChangeConflict(
  error,
  initialRequestBody,
  { staleCodes = ["source_changes_preview_stale"] } = {},
) {
  const normalizedStaleCodes = normalizeStaleCodes(staleCodes);
  const isStale = Number(error?.status) === 409
    && normalizedStaleCodes.has(normalizeInlineText(error?.code));
  if (!isStale) {
    return {
      hasCompleteSelection: false,
      isStale: false,
      retryRequestBody: null,
      selectionMatches: false,
    };
  }

  const previewRevision = normalizeInlineText(readConflictPayloadField(error, "preview_revision"));
  const serverOrderIds = normalizeExactOrderIds(readConflictPayloadField(error, "order_ids"));
  const initialOrderIds = normalizeExactOrderIds(initialRequestBody?.order_ids);
  const hasCompleteSelection = Boolean(previewRevision) && serverOrderIds !== null;
  const selectionMatches = hasCompleteSelection
    && initialOrderIds !== null
    && haveSameSourceChangeOrderIds(initialOrderIds, serverOrderIds);

  return {
    hasCompleteSelection,
    isStale: true,
    retryRequestBody: selectionMatches
      ? {
        ...initialRequestBody,
        order_ids: [...initialRequestBody.order_ids],
        preview_revision: previewRevision,
      }
      : null,
    selectionMatches,
  };
}

export async function executeExactSelectionSourceChangeRequest({
  canRetry,
  initialRequestBody,
  postRequest,
  refreshSelection,
  staleCodes = ["source_changes_preview_stale"],
} = {}) {
  if (typeof postRequest !== "function") {
    throw new TypeError("La fonction de mutation M-à-J est requise.");
  }

  try {
    return await postRequest(initialRequestBody);
  } catch (error) {
    const conflict = resolveExactSourceChangeConflict(error, initialRequestBody, { staleCodes });
    if (!conflict.isStale) {
      throw error;
    }

    let retryRequestBody = conflict.retryRequestBody;
    let didRefresh = false;
    if (!retryRequestBody && !conflict.hasCompleteSelection && typeof refreshSelection === "function") {
      let refreshedSelection = null;
      try {
        refreshedSelection = await refreshSelection();
        didRefresh = true;
      } catch (refreshError) {
        markSourceChangesRefreshPerformed(refreshError);
        throw refreshError;
      }
      const refreshedPreviewRevision = normalizeInlineText(
        refreshedSelection?.preview_revision || refreshedSelection?.previewRevision,
      );
      const refreshedOrderIds = normalizeExactOrderIds(
        refreshedSelection?.order_ids || refreshedSelection?.orderIds,
      );
      if (
        refreshedPreviewRevision
        && refreshedOrderIds !== null
        && haveSameSourceChangeOrderIds(initialRequestBody?.order_ids, refreshedOrderIds)
      ) {
        retryRequestBody = {
          ...initialRequestBody,
          order_ids: [...initialRequestBody.order_ids],
          preview_revision: refreshedPreviewRevision,
        };
      }
    }

    const isRetryAllowed = retryRequestBody
      && (typeof canRetry !== "function" || canRetry() !== false);
    if (!retryRequestBody || !isRetryAllowed) {
      if (didRefresh) {
        markSourceChangesRefreshPerformed(error);
      }
      throw error;
    }

    try {
      return await postRequest(retryRequestBody);
    } catch (retryError) {
      markSourceChangesRetryAttempted(retryError);
      if (didRefresh) {
        markSourceChangesRefreshPerformed(retryError);
      }
      throw retryError;
    }
  }
}
